'use client'

import { compact, debounce, isEqual } from 'lodash'
import { useEffect, useRef, useState } from 'react'
import { ComboBoxView } from './combo-box'
import { bg } from 'lib/background'
import { OptionType } from 'types/OptionType'

const DEFAULT_DESELECT_ALL_OPTION = { name: `All`, id: `0`, value: `` }

export type OptionsFetchResponse = {
  options?: OptionType[]
  error?: string
}

export type ComboboxMultipleProps = {
  name?: string
  disabled?: boolean
  optionsMapper: (res: any) => OptionType[]
  placeholder?: string
  queryURL?: string
  /**
   * Optional async loader. When supplied, calls it instead of `fetch(queryURL?...)`.
   * Receives search text and any additionalParams. Must return a payload for `optionsMapper`.
   */
  fetcher?: (params: { query: string; [key: string]: any }) => Promise<any>
  additionalParams?: { [key: string]: string }
  values?: string[]
  queryValues?: boolean
  additionalChoices?: OptionType[]
  className?: string
  inputClassName?: string
  optionsContainerClassName?: string
  onChangeHandler: (options: OptionType[]) => void
  icon?: React.ReactElement
  enableDeselectAllOption?: boolean
  deselectAllOption?: OptionType
  defaultOpen?: boolean
  isFilter?: boolean
  withTooltip?: boolean
  checkboxSide?: `left` | `right`
  customLabel?: string
  customButton?: React.ReactNode
  popoverStyle?: React.CSSProperties
  isOptionsGrouped?: boolean
}

function defaultFetcher(queryURL: string, params: Record<string, string>): Promise<any> {
  const qs = new URLSearchParams(params).toString()
  return window.fetch(`${queryURL}?${qs}`, { credentials: `include` }).then((r) => r.json())
}

export default function ComboMultiSelectAsync({
  name,
  disabled = false,
  optionsMapper,
  placeholder,
  queryURL,
  fetcher,
  additionalParams,
  values,
  queryValues = true,
  additionalChoices,
  className,
  inputClassName,
  optionsContainerClassName,
  onChangeHandler,
  icon,
  enableDeselectAllOption = false,
  defaultOpen = false,
  deselectAllOption = DEFAULT_DESELECT_ALL_OPTION,
  isFilter = false,
  withTooltip = false,
  checkboxSide,
  customLabel,
  customButton,
  popoverStyle,
  isOptionsGrouped = false,
}: ComboboxMultipleProps) {
  const loadOptions = (params: Record<string, string>): Promise<any> => {
    if (fetcher) return fetcher(params as { query: string; [key: string]: any })
    if (queryURL) return defaultFetcher(queryURL, params)
    return Promise.resolve({})
  }

  const requestSequence = useRef(0)
  const [data, setData] = useState<OptionType[]>(
    enableDeselectAllOption && deselectAllOption ? [deselectAllOption] : [],
  )
  // When we don't re-query the endpoint for the selected values (queryValues=false),
  // seed the selection synchronously in the initializer so the correct label paints
  // on the FIRST render. Otherwise the effect below only seeds it post-paint, which
  // leaves the placeholder visible for one frame. queryValues=true keeps the old
  // empty start (the effect resolves the labels asynchronously).
  const [selectedOptions, setSelectedOptions] = useState<OptionType[]>(() => {
    if (queryValues) return []
    if (values && compact(values).length) {
      if (enableDeselectAllOption && isEqual(values, [`*`])) return [deselectAllOption]
      return optionsMapper(values)
    }
    return enableDeselectAllOption && deselectAllOption ? [deselectAllOption] : []
  })
  const [firstOpen, setFirstOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [query, setQuery] = useState(``)

  const applyFetchResult = (res: any, sequence: number) => {
    if (sequence !== requestSequence.current) return
    const options = optionsMapper(res)
    if (compact(options).length) {
      const seenIds = new Set(selectedOptions.map((o) => o.id))
      const filteredOptions = options.concat(deselectAllOption).filter((option) => {
        if (seenIds.has(option.id)) return false
        seenIds.add(option.id)
        return true
      })
      setData(compact([...selectedOptions, ...(additionalChoices || []), ...filteredOptions]))
    } else if (query) {
      // A search that matches nothing has to empty the list so the "No results found"
      // state shows. Keeping the previous options reads as the search being ignored.
      setData([])
    }
    setLoading(false)
  }

  const loadOptionsForParams = (params: Record<string, string>) => {
    const sequence = ++requestSequence.current
    setLoading(true)
    return loadOptions(params)
      .then((res) => applyFetchResult(res, sequence))
      .catch(() => {
        if (sequence === requestSequence.current) setLoading(false)
      })
  }

  useEffect(() => {
    loadOptionsForParams({ query, ...additionalParams })
  }, [query])

  useEffect(() => {
    if (!firstOpen) return
    loadOptionsForParams(additionalParams ?? {})
  }, [firstOpen])

  const additionalParamsKey = JSON.stringify(additionalParams)
  useEffect(() => {
    loadOptionsForParams(additionalParams ?? {})
  }, [additionalParamsKey])

  useEffect(() => {
    if (queryValues && !!values && compact(values).length) {
      if (enableDeselectAllOption && isEqual(values, [`*`])) {
        setSelectedOptions([deselectAllOption])
        return
      }
      const terms = values?.map((term) => `"${term}"`).join(` OR `)
      bg(
        loadOptions({ query: terms }).then(async (response) => {
          const loadedOptions = optionsMapper(response)
          setSelectedOptions(loadedOptions)
        }),
      )
    } else if (compact(values).length === 0) {
      setSelectedOptions(enableDeselectAllOption ? [deselectAllOption] : [])
    } else {
      // queryValues=false: the value is already the display string, so keep the
      // selection in sync with every non-empty `values` change (e.g. browser
      // back/forward switching from one channel to another) — not only the first
      // non-empty value (the previous `selectedOptions.length === 0` guard left a
      // changed value showing the stale selection). Compare by value so a change
      // in only the array's identity doesn't reset state and churn renders.
      const next = optionsMapper(values)
      setSelectedOptions((prev) =>
        prev.length === next.length && prev.every((option, i) => option.value === next[i]?.value) ? prev : next,
      )
    }
  }, [values, enableDeselectAllOption])

  const onChange = (options: OptionType[]) => {
    if (
      options.find((opt) => opt.value === deselectAllOption.value) &&
      !selectedOptions.find((opt) => opt.value === deselectAllOption.value)
    ) {
      setSelectedOptions([deselectAllOption])
      onChangeHandler([deselectAllOption])
    } else if (
      options.find((opt) => opt.value === deselectAllOption.value) &&
      selectedOptions.find((opt) => opt.value === deselectAllOption.value)
    ) {
      const opts = options.filter((opt) => opt.value !== deselectAllOption.value)
      setSelectedOptions(opts)
      onChangeHandler(opts)
    } else {
      setSelectedOptions(options)
      onChangeHandler(options)
    }
  }

  return (
    <ComboBoxView
      name={name}
      className={className}
      inputClassName={inputClassName}
      optionsContainerClassName={optionsContainerClassName}
      options={data}
      placeholder={placeholder}
      selected={selectedOptions}
      loading={loading}
      enableDeselectAllOption={enableDeselectAllOption}
      deselectAllOptions={[deselectAllOption]}
      disabled={disabled}
      customIcon={icon}
      chevronClassName={isFilter ? `pr-6` : undefined}
      multiple
      showButtonTooltip={withTooltip}
      checkboxSide={checkboxSide}
      customLabel={customLabel}
      customButton={customButton}
      popoverStyle={popoverStyle}
      isOptionsGrouped={isOptionsGrouped}
      open={defaultOpen}
      onChangeMultiple={onChange}
      onClearSearch={() => setQuery(``)}
      onSearchValueChange={debounce((text) => {
        setQuery(text)
      }, 300)}
      onButtonClick={() => setFirstOpen(true)}
    />
  )
}
